import { createTRPCClient, httpLink } from "@trpc/client";
import type { AppRouter } from "@/trpc/routerTypes";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { QueryClient } from "@tanstack/react-query";
import Config from "@/config/config";
import { auth } from "#/firebase/app";
import { buildAuthHeaders } from "@/trpc/authHeaders";
import { mintTestAuthToken, readTestAuthSession } from "@/lib/testAuth";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpLink({
      url: "",
      headers() {
        // #441: an injected dev session has no Firebase user, so `auth.currentUser` is
        // null and buildAuthHeaders would send an empty token. Mint the server's
        // `gs-dev-test:` token instead — the only place the client produces one.
        // Statically false in a production build → the branch and the module go away.
        const testSession = import.meta.env.DEV ? readTestAuthSession() : null;
        if (testSession) {
          return { authorization: mintTestAuthToken(testSession) };
        }
        return buildAuthHeaders(auth);
      },
      fetch(url, options) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        const normalisedUrl = url.toString().replace(".", "/");
        const splitUrl = normalisedUrl.split("/");
        const [router, procedure] = [splitUrl[1], splitUrl[2]];
        return fetch(`${Config.trpc_url}/${router}/${procedure}`, options);
      },
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});
